'use client';

import { useEffect, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import useUser from 'grandus-lib/hooks/useUser';
import useStack from 'utils/useStack';
import { getCategoryLinkAttributes } from 'grandus-lib/hooks/useFilter';

import Link from 'next/link';
import Image from 'grandus-lib/components-atomic/image/Image';
import LoginIcon from 'components/_other/icons/LoginIcon';
import CloseIcon from 'components/_other/icons/CloseIcon';
import styles from 'app/components/header/menu/Mobile.module.scss';
import Drawer from 'components/_other/drawer/Drawer';
import ArrowLeftIcon from 'components/_other/icons/ArrowLeftIcon';
import { usePathname } from 'next/navigation';

const CloseButton = ({ closeMenu }) => (
  <button
    className={styles?.closeButton}
    onClick={() => {
      closeMenu();
      return false;
    }}
  >
    <CloseIcon className={styles.closeIcon} />
  </button>
);

const MenuItem = ({ category, handleCategoryClick, level }) => {
  const hasSubmenu = get(category, 'children', []).length > 0;
  return (
    <Link
      {...getCategoryLinkAttributes(
        category?.urlName,
        '',
        {},
        { absoluteHref: get(category, 'externalUrl') },
      )}
      scroll={true}
      onClick={e => {
        if (hasSubmenu) {
          e.preventDefault();
          handleCategoryClick(category);
        }
      }}
    >
      <span className={styles?.menuItemLink}>{category?.name}</span>
      <span className={styles?.image}>
        <Image
          useNextImage
          photo={category?.mainPhoto}
          size="35x35@2x"
          type={'jpg'}
          alt={category?.mainPhoto?.description || category?.name}
        />
      </span>
    </Link>
  );
};

const Submenu = ({
  parent,
  handleCategoryClick,
  handleBackClick,
  level = 0,
}) => {
  if (isEmpty(parent?.children)) {
    return null;
  }

  return (
    <ul>
      <li className={styles?.header}>
        <a href="#" onClick={handleBackClick} className={styles?.back}>
          <span>
            <ArrowLeftIcon />
          </span>
          {parent?.name}
        </a>
      </li>
      <li>
        <Link href={`/kategoria/${parent?.urlName}`} className={styles?.all}>
          Zobraziť všetko z kategórie
        </Link>
      </li>
      {map(parent?.children, category => (
        <li key={`category-${category?.id}`}>
          <MenuItem
            level={level}
            category={category}
            handleCategoryClick={handleCategoryClick}
            key={`category-${category?.id}`}
          />
        </li>
      ))}
    </ul>
  );
};

const Menu = ({ options = {}, categories }) => {
  const {
    data: menuStack,
    pop: popStack,
    push: pushStack,
    peek: peekStack,
    isEmpty: isEmptyStack,
    truncate: truncateStack,
    length: lengthStack,
  } = useStack();

  const isStackEmpty = isEmptyStack();
  const pathname = usePathname();

  const [isOpen, setIsOpen] = useState(true);

  const closeMenu = () => {
    setIsOpen(false);
  };

  // make page body fixed (do not scoll)
  useEffect(() => {
    if (!isOpen) {
      truncateStack();
    }

    return () => {
      truncateStack();
    };
  }, [isOpen]);

  // close menu on route change
  useEffect(() => {
    const handleRouteChange = () => {
      closeMenu();
    };

    handleRouteChange();
  }, [pathname]);

  useEffect(() => {
    const handleButtonOpen = () => {
      setIsOpen(true);
    };

    document.addEventListener('mobile-menu-open', handleButtonOpen);

    return () => {
      document.removeEventListener('mobile-menu-open', handleButtonOpen);
    };
  }, []);

  const onClickToggleOpenCategory = category => {
    pushStack(category);
  };

  const onBackClick = e => {
    e.preventDefault();
    popStack();
  };

  return (
    <Drawer
      position="left"
      active={isOpen}
      handleClose={closeMenu}
      className={styles?.customDrawer}
      contentClassName={styles?.customDrawerContent}
      closeButton={<CloseButton closeMenu={closeMenu} />}
    >
      <div className={`${styles?.menu}`}>
        {/*<TopActions />*/}
        <nav>
          {!isEmpty(categories) && isStackEmpty ? (
            <ul>
              {categories.map(category => {
                return (
                  <li key={`category-${category?.id}`}>
                    <MenuItem
                      level={0}
                      category={category}
                      handleCategoryClick={onClickToggleOpenCategory}
                      key={`category-${category?.id}`}
                    />
                  </li>
                );
              })}
            </ul>
          ) : null}
          {!isStackEmpty ? (
            <Submenu
              parent={peekStack()}
              handleCategoryClick={onClickToggleOpenCategory}
              handleBackClick={onBackClick}
              level={lengthStack}
            />
          ) : null}
        </nav>
      </div>
    </Drawer>
  );
};

export default Menu;
